import { Dimensions } from './GameCanvas';
import { Portal, TransferableEntity } from '@antification/protocol';
import { EventBus } from '@antification/core';

/**
 * Interface for game layers that can be registered with LayerManager.
 * Each layer is an independent game simulation that can interact with other layers
 * through the portal system and event bus.
 */
export interface GameLayer {
  /** Unique identifier for this layer (e.g., 'parkington', 'formicarium') */
  readonly id: string;

  /** Human-readable display name (e.g., 'Parkington', 'Formicarium') */
  readonly displayName: string;

  /** Initialize the layer (load assets, set up state, etc.) */
  init(): Promise<void>;

  /** Update game logic for this layer */
  update(dt: number): void;

  /** Render the layer to its canvas context */
  render(ctx: CanvasRenderingContext2D): void;

  /** Handle viewport resize */
  resize(dimensions: Dimensions): void;

  // Optional input handlers - called by LayerManager when this layer is active/focused
  handleMouseMove?(e: MouseEvent): void;
  handleClick?(e: MouseEvent): void;
  handleMouseDown?(e: MouseEvent): void;
  handleMouseUp?(e: MouseEvent): void;
  handleKeyDown?(e: KeyboardEvent): void;
  handleKeyUp?(e: KeyboardEvent): void;

  /** Get all portals defined by this layer */
  getPortals(): Portal[];

  /**
   * Receive an entity from another layer via portal.
   *
   * Returns whether this layer took it. A layer may refuse: since ADR-028 a
   * surface entrance under a facility's foundation is closed, and an ant that
   * cannot arrive must not be counted as having arrived — the colony it came from
   * decides whether it is above ground by asking whether the transfer landed.
   */
  receiveEntity(entity: TransferableEntity, fromPortal: string): boolean;
}

/**
 * Base class for game layers providing common functionality.
 * Extend this class and implement abstract methods for your layer.
 */
export abstract class BaseGameLayer implements GameLayer {
  abstract readonly id: string;
  abstract readonly displayName: string;

  protected events: EventBus;
  protected width = 0;
  protected height = 0;
  protected portals: Portal[] = [];

  constructor(events: EventBus) {
    this.events = events;
  }

  abstract init(): Promise<void>;
  abstract update(dt: number): void;
  abstract render(ctx: CanvasRenderingContext2D): void;

  resize(dimensions: Dimensions): void {
    this.width = dimensions.width;
    this.height = dimensions.height;
  }

  getPortals(): Portal[] {
    return this.portals;
  }

  receiveEntity(_entity: TransferableEntity, _fromPortal: string): boolean {
    // Override in subclass to handle incoming entities. A layer that has not is a
    // layer nothing can arrive at.
    return false;
  }

  /**
   * Register a portal for this layer.
   * Call this during init() to set up connections to other layers.
   */
  protected registerPortal(portal: Portal): void {
    this.portals.push(portal);
  }

  /**
   * Remove a portal by ID.
   */
  protected unregisterPortal(portalId: string): void {
    const index = this.portals.findIndex(p => p.id === portalId);
    if (index !== -1) {
      this.portals.splice(index, 1);
    }
  }
}
